import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Icon } from '@iconify/react'

import { Form, Modal, Select, Space, Tag, message } from 'antd'
import { CANDIDATE_STATUS } from './candidate-status-tab-list'
import ReactQuill from 'react-quill'
import {
    candidateAddNote,
    candidateById,
    candidateEdit,
} from '../store/candidate.slice'
import { uploadResume, uploadResumeS3Url } from '../service/candidate.service'

import ConverToUserForm from './convert-user-form'
import { getJobApplicantListByStatus } from '../../jobApplications/store/jobApplicant.slice'
import styles from './status-modal.module.css'

const StatusModal = ({
    open,
    onClose,
    onChange,
    selectedStatus,
    statusModal,
    isDetailsApi,
}) => {
    const dispatch = useDispatch()
    const [noteInput, setNoteInput] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const { userProfile } = useSelector((state) => state.loginSlice)
    const [attachments, setAttachments] = useState([])
    const { candidateId, activeTab, jobId } = useSelector(
        (state) => state.candidateSlice
    )
    const { applicantSearch } = useSelector((state) => state.jobApplicantSlice)

    const { state } = useLocation()

    const handleSaveNoteWithAttachments = async (
        candidateId,
        fieldName = 'add'
    ) => {
        try {
            let uploadedLinks = null

            if (attachments.length) {
                const payload = {
                    data: attachments.map((f) => ({
                        filename: f.name,
                        contentType: f.type,
                    })),
                }

                const { data } = await uploadResume(payload)

                if (data.success) {
                    uploadedLinks = []

                    for (const file of attachments) {
                        const match = data.data.find(
                            (item) => item.filename === file.name
                        )
                        if (!match) {
                            console.error(
                                `No upload URL for file: ${file.name}`
                            )
                            continue
                        }

                        const response = await uploadResumeS3Url(
                            file,
                            match.uploadUrl,
                            match.publicLink,
                            match.name
                        )
                        if (response.ok) {
                            uploadedLinks.push({
                                title: file.name,
                                displayName: file.name,
                                category: 'document',
                                file: {
                                    fileName: file.name,
                                    fileLink: match.publicLink,
                                    fileType: file.type,
                                    uploadDate: Date.now(),
                                },
                            })
                        } else {
                            console.error(
                                `Failed to upload ${file.name}:`,
                                await response.text()
                            )
                        }
                    }

                    if (uploadedLinks.length === 0) uploadedLinks = null
                }
            }

            await handleAddNote(candidateId, uploadedLinks, fieldName)
        } catch (error) {
            console.error('Error saving note with attachments:', error)
        }
    }

    const [form] = Form.useForm()
    const email = Form.useWatch('email', form)
    const password = Form.useWatch('password', form)
    const confirmPassword = Form.useWatch('confirmPassword', form)
    const timeZone = Form.useWatch('timeZone', form)
    const location = Form.useWatch('location', form)
    const nationality = Form.useWatch('nationality', form)

    const hasValue = (value) =>
        typeof value === 'string' ? value.trim().length > 0 : Boolean(value)

    const requiredFieldsFilled =
        selectedStatus !== 'convertToUser' ||
        [email, password, confirmPassword, timeZone, location, nationality].every(
            hasValue
        )

    const submitDisabled = submitting || !requiredFieldsFilled

    const handleSubmit = async (status) => {
        if (status === 'convertToUser') {
            const values = await form.validateFields()
            let finalEmail = values.email
            if (!finalEmail.endsWith('@softwareco.com')) {
                finalEmail = `${finalEmail}@softwareco.com`
            }

            const payload = {
                status: status,
                candidateId: statusModal?.candidate?.candidateId,
                organisationEmail: finalEmail,
                password: values.confirmPassword,
                timeZone: values.timeZone,
                location: values.location,
                nationality: values.nationality,
            }
            const { data } = await candidateEdit(
                'STATUS',
                statusModal?.candidate?._id,
                payload
            )
            if (data.success) {
                if (!isDetailsApi) {
                    dispatch(
                        getJobApplicantListByStatus({
                            filter: {
                                status: activeTab,
                                job: jobId,
                                search: applicantSearch,
                            },
                        })
                    )
                } else if (candidateId) {
                    dispatch(
                        candidateById({
                            id: candidateId,
                            activeTab: 'OVERVIEW',
                        })
                    )
                }
            } else {
                console.error('Error updating candidate status:', data.message)
            }
        } else {
            try {
                const payload = {
                    status: status,
                    candidateId:
                        state?.candidateId ||
                        statusModal?.candidate?.candidateId,
                    jobId: state?.jobId || statusModal?.candidate?.jobId,
                }
                await candidateEdit(
                    'STATUS',
                    statusModal?.candidate?.candidateId,
                    payload
                )
                if (!isDetailsApi) {
                    dispatch(
                        getJobApplicantListByStatus({
                            filter: {
                                status: activeTab,
                                job: jobId,
                                search: applicantSearch,
                            },
                        })
                    )
                } else if (candidateId) {
                    dispatch(
                        candidateById({
                            id: candidateId,
                            activeTab: 'OVERVIEW',
                        })
                    )
                }
            } catch (error) {
                console.log('🚀 ~ handleSubmit ~ error:', error)
            }
        }
    }

    const handleAddNote = async (
        candidateId,
        attachments = null,
        fieldName = 'add'
    ) => {
        if (!noteInput.trim()) return

        try {
            const payload = {
                candidateComment: {
                    content: noteInput,
                    createdDate: Date.now(),
                    status: statusModal.status,
                    createdBy: userProfile._id,
                    attachments: attachments,
                },
                fieldName,
            }

            const { data } = await candidateAddNote(candidateId, payload)

            if (data.success) {
                setNoteInput('')
                setAttachments([])
            }
        } catch (error) {
            console.error('Error adding note:', error)
        }
    }

    const handleOnChange = (value) => {
        setNoteInput(value)
    }

    const handleCloseModel = () => {
        if (submitting) return
        onClose()
        setNoteInput('')
        setSubmitting(false)
    }

    const handleConfirm = async () => {
        if (submitDisabled) return
        setSubmitting(true)
        try {
            await handleSubmit(statusModal.status)
            await handleSaveNoteWithAttachments(statusModal.candidate._id)
            onClose()
            setNoteInput('')
        } catch (error) {
            if (error?.response?.data?.message) {
                message.error(error?.response?.data?.message)
            }
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Modal
            open={open}
            title={
                <div className={styles.modalHeader}>
                    <div className={styles.modalHeaderLeft}>
                        <div className={styles.modalHeaderIcon}>
                            <Icon
                                icon="mdi:swap-horizontal"
                                width={20}
                                height={20}
                            />
                        </div>
                        <div>
                            <div className={styles.modalHeaderTitle}>
                                Change Candidate Status
                            </div>
                            <div className={styles.modalHeaderSubtitle}>
                                Update pipeline stage and leave an optional note
                            </div>
                        </div>
                    </div>
                </div>
            }
            onCancel={handleCloseModel}
            footer={
                <div className={styles.modalFooter}>
                    <div className={styles.formActions}>
                        <button
                            type="button"
                            onClick={handleCloseModel}
                            disabled={submitting}
                            className={styles.cancelButton}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            disabled={submitDisabled}
                            onClick={handleConfirm}
                            className={`${styles.submitButton} ${
                                submitDisabled ? styles.submitButtonDisabled : ''
                            }`}
                        >
                            {submitting ? (
                                <>
                                    <Icon
                                        icon="line-md:loading-loop"
                                        width={16}
                                        height={16}
                                        className={styles.submitSpinner}
                                    />
                                    Updating...
                                </>
                            ) : (
                                'Update Status'
                            )}
                        </button>
                    </div>
                </div>
            }
            width={560}
            className={styles.statusModal}
            destroyOnClose
            centered
            maskClosable={!submitting}
            closable={!submitting}
        >
            <div className={styles.modalBody}>
                <div className={styles.field}>
                    <label className={styles.fieldLabel} htmlFor="candidate-status">
                        Status
                    </label>
                    <Select
                        id="candidate-status"
                        className={styles.statusSelect}
                        value={selectedStatus}
                        onChange={(value) => onChange(value)}
                        placeholder="Select status"
                        optionLabelProp="label"
                    >
                        {CANDIDATE_STATUS.map((status) => (
                            <Select.Option
                                key={status.value}
                                value={status.value}
                                label={status.label}
                            >
                                <Space>
                                    <Tag color={status.color}>{status.label}</Tag>
                                </Space>
                            </Select.Option>
                        ))}
                    </Select>
                </div>

                {selectedStatus === 'convertToUser' && (
                    <div className={styles.field}>
                        <ConverToUserForm form={form} />
                    </div>
                )}

                <div className={styles.field}>
                    <label className={styles.fieldLabel}>Note</label>
                    <ReactQuill
                        theme="snow"
                        className={styles.noteEditor}
                        onChange={handleOnChange}
                        value={noteInput}
                        placeholder="Write a note..."
                    />
                </div>
            </div>
        </Modal>
    )
}

export default StatusModal
